import { useState, useEffect } from 'react';
import { X, FileText, Package, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';
import { useToast } from './ui';

const Form103Modal = ({ truck, isOpen, onClose, onSuccess }) => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [poLines, setPoLines] = useState([]);
  const [qcConfig, setQcConfig] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && truck) {
      fetchPOLines();
    }
  }, [isOpen, truck]);

  const fetchPOLines = async () => {
    try {
      setFetchingData(true);
      setErrors({});
      const response = await api.get(`/qc/grn/po-lines-for-truck/${truck.id}/`);

      setPoLines(response.data.po_lines);
      setQcConfig(response.data.qc_configuration);

      // Initialize invoice items with remaining quantities as default
      setInvoiceItems(
        response.data.po_lines.map(line => ({
          po_line_id: line.id,
          material_code: line.material.code,
          material_description: line.material.description,
          unit_of_measure: line.material.unit_of_measure,
          ordered_quantity: parseFloat(line.ordered_quantity),
          remaining_quantity: parseFloat(line.remaining_quantity),
          invoice_quantity: parseFloat(line.remaining_quantity),
          unit_price: parseFloat(line.unit_price),
          batch_number: '',
          vendor_batch: '',
          manufacturing_date: '',
          expiry_date: '',
          notes: ''
        }))
      );
    } catch (err) {
      console.error('Error fetching PO lines:', err);
      showError(err.response?.data?.error || 'Failed to load PO lines');
      if (err.response?.data?.existing_grn) {
        setTimeout(() => onClose(), 2000);
      }
    } finally {
      setFetchingData(false);
    }
  };

  const updateField = (index, field, value) => {
    const updated = [...invoiceItems];
    updated[index][field] = value;
    setInvoiceItems(updated);

    // Clear error for this field
    if (errors[`item_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`item_${index}_${field}`];
      setErrors(newErrors);
    }
  };

  const validateAndSubmit = async () => {
    const validationErrors = {};

    // Validate each line item
    invoiceItems.forEach((item, idx) => {
      if (!item.invoice_quantity || item.invoice_quantity <= 0) {
        validationErrors[`item_${idx}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.invoice_quantity > item.ordered_quantity) {
        validationErrors[`item_${idx}_quantity`] =
          `Cannot exceed PO quantity (${item.ordered_quantity})`;
      }
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showError('Please fix validation errors');
      return;
    }

    // Submit
    try {
      setLoading(true);
      setErrors({});

      const payload = {
        truck_arrival_id: truck.id,
        invoice_line_items: invoiceItems.map(item => ({
          po_line_id: item.po_line_id,
          quantity: item.invoice_quantity,
          batch_number: item.batch_number || '',
          vendor_batch: item.vendor_batch || '',
          manufacturing_date: item.manufacturing_date || null,
          expiry_date: item.expiry_date || null,
          notes: item.notes || ''
        }))
      };

      const response = await api.post('/qc/grn/create-form-103/', payload);

      success(`Form 103 ${response.data.summary.grn_number} created successfully!`);
      onSuccess(response.data);
      onClose();
    } catch (err) {
      console.error('Error creating Form 103:', err);
      const errorMsg = err.response?.data?.error || 'Failed to create Form 103';
      showError(errorMsg);
      if (err.response?.data) {
        setErrors({ general: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return invoiceItems.reduce((sum, item) =>
      sum + (item.invoice_quantity * item.unit_price), 0
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-7xl w-full my-8 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10 rounded-t-lg">
          <div>
            <h2 className="text-2xl font-bold flex items-center">
              <FileText className="w-6 h-6 mr-2 text-nexgensis-primary" />
              Create Form 103 - Invoice Entry
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Enter complete material details from supplier invoice
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {fetchingData ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nexgensis-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Loading PO lines...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Truck & Invoice Info */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs text-gray-600">Truck Number</label>
                  <p className="font-semibold">{truck.truck_number}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Invoice Number</label>
                  <p className="font-semibold">{truck.invoice_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-600">PO Number</label>
                  <p className="font-semibold">{truck.purchase_order?.po_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Supplier</label>
                  <p className="font-semibold">{truck.supplier?.name || 'N/A'}</p>
                </div>
              </div>

              {/* QC Configuration */}
              {qcConfig && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900 mb-2">QC Configuration</p>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-blue-700">Stock Type:</span>
                          <span className="font-semibold ml-2">BLOCKED (Quality Inspection)</span>
                        </div>
                        <div>
                          <span className="text-blue-700">QC Level:</span>
                          <span className={`font-semibold ml-2 ${
                            qcConfig.qc_inspection_level === 'CRITICAL' ? 'text-red-600' : 'text-blue-900'
                          }`}>
                            {qcConfig.qc_inspection_level}
                          </span>
                        </div>
                        <div>
                          <span className="text-blue-700">Sampling:</span>
                          <span className="font-semibold ml-2">{qcConfig.qc_sampling_plan}</span>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        {qcConfig.qc_determination_reason}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* General Error */}
              {errors.general && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                  <p className="text-red-800 text-sm">{errors.general}</p>
                </div>
              )}

              {/* Invoice Line Items Table */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Invoice Line Items
                  <span className="text-sm text-gray-500 ml-2 font-normal">
                    Enter quantities and batch details from supplier invoice
                  </span>
                </h3>

                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoice Qty</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor Batch</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mfg Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoiceItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-semibold text-sm">{item.material_code}</div>
                              <div className="text-xs text-gray-500">{item.material_description}</div>
                              <div className="text-xs text-gray-400 mt-1">
                                PO: {item.ordered_quantity} | Remaining: {item.remaining_quantity}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={item.invoice_quantity}
                              onChange={(e) => updateField(index, 'invoice_quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              max={item.ordered_quantity}
                              step="1"
                              className={`input-field w-24 text-right ${
                                errors[`item_${index}_quantity`] ? 'border-red-500' : ''
                              }`}
                              required
                            />
                            {errors[`item_${index}_quantity`] && (
                              <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_quantity`]}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={item.batch_number}
                              onChange={(e) => updateField(index, 'batch_number', e.target.value)}
                              placeholder="From delivery note"
                              className="input-field w-32 text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={item.vendor_batch}
                              onChange={(e) => updateField(index, 'vendor_batch', e.target.value)}
                              placeholder="From label"
                              className="input-field w-32 text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="date"
                              value={item.manufacturing_date}
                              onChange={(e) => updateField(index, 'manufacturing_date', e.target.value)}
                              max={new Date().toISOString().split('T')[0]}
                              className="input-field w-36 text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="date"
                              value={item.expiry_date}
                              onChange={(e) => updateField(index, 'expiry_date', e.target.value)}
                              min={item.manufacturing_date || new Date().toISOString().split('T')[0]}
                              className="input-field w-36 text-sm"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={item.notes}
                              onChange={(e) => updateField(index, 'notes', e.target.value)}
                              placeholder="Observations"
                              className="input-field w-full text-sm"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan="6" className="px-4 py-3 text-right font-semibold">
                          Total Value:
                        </td>
                        <td className="px-4 py-3 font-bold text-lg">
                          ₹{calculateTotal().toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Helper Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
                <p className="font-semibold text-yellow-900 mb-2">📋 Data Entry Guide:</p>
                <ul className="text-sm text-yellow-800 space-y-1 ml-4">
                  <li>• Check supplier invoice for quantities</li>
                  <li>• Scan/read batch numbers from delivery note</li>
                  <li>• Check material labels for vendor batch</li>
                  <li>• Refer to supplier certificate for dates</li>
                  <li>• Note any packaging damage or observations</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={validateAndSubmit}
                  className="btn-primary flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Create Form 103
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Form103Modal;
